import { cursor } from "../components/cursor";
import { revealText } from "../base/split-text";
import { isTouchDevice, prefersReducedMotion, varGutter } from "./variables";

const boundDatasetKey = "marqueeBound";
const preloadRootMargin = "800px 0px";
const minStripCopies = 3;
const maxStripCopies = 12;
const referenceWidth = 1440;
const driftSpeedVw = -2;
const momentumScaleVw = -16.5;
const maxSpeedVw = 260;
const deadzone = 0.001;
const wheelScale = 10;
const wheelDecay = 250;
const velocitySmoothing = 10;
const maxWheelVelocity = 1000;
const maxTouchMomentum = 4000;
const touchMomentumDeadzone = 1;
const slideVelocityScale = 600;
const slideVelocitySmoothing = 8;
const slideVelocityDeadzone = 0.001;
const slideVelocities = new WeakMap<HTMLElement, number>();

export function getMarqueeVelocity(wrapper: HTMLElement): number {
    return slideVelocities.get(wrapper) ?? 0;
}

export async function initMarqueeSlider(slider: HTMLElement): Promise<void> {
    if (slider.dataset[boundDatasetKey] === "true") return;
    slider.dataset[boundDatasetKey] = "true";

    const wrapper = slider.querySelector<HTMLElement>(".swiper-wrapper");
    if (!wrapper) return;

    await whenNearViewport(slider);
    loadDeferredImages(wrapper);
    await waitForImages(wrapper.querySelectorAll("img"));

    const unitWidth = buildLoopStrip(wrapper, slider.clientWidth);
    bindMarquee(slider, wrapper, unitWidth);
}

function whenNearViewport(el: HTMLElement): Promise<void> {
    if (typeof IntersectionObserver === "undefined") return Promise.resolve();

    return new Promise(resolve => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    observer.disconnect();
                    resolve();
                }
            },
            { rootMargin: preloadRootMargin }
        );

        observer.observe(el);
    });
}

function loadDeferredImages(wrapper: HTMLElement) {
    wrapper.querySelectorAll<HTMLImageElement>("img[data-src]").forEach(img => {
        const src = img.dataset.src;
        if (!src) return;

        img.src = src;
        img.removeAttribute("data-src");
    });

    wrapper.querySelectorAll<HTMLImageElement>('img[loading="lazy"]').forEach(img => {
        img.loading = "eager";
    });
}

function waitForImages(images: NodeListOf<HTMLImageElement>): Promise<void> {
    const pending = [...images].map(img => {
        if (img.complete) return Promise.resolve();

        return new Promise<void>(resolve => {
            img.addEventListener("load", () => resolve(), { once: true });
            img.addEventListener("error", () => resolve(), { once: true });
        });
    });

    return Promise.all(pending).then(() => undefined);
}

function buildLoopStrip(wrapper: HTMLElement, containerWidth: number): number {
    const originalSlides = [...wrapper.children] as HTMLElement[];
    if (originalSlides.length === 0) return 0;

    const unitWidth = originalSlides.reduce((sum, el) => sum + el.offsetWidth, 0);
    if (unitWidth <= 0) return 0;

    const copiesNeeded = Math.min(maxStripCopies, Math.max(minStripCopies, Math.ceil((containerWidth * 2) / unitWidth) + 1));

    for (let copy = 1; copy < copiesNeeded; copy++) {
        originalSlides.forEach(slide => {
            const clone = slide.cloneNode(true) as HTMLElement;
            clone.setAttribute("aria-hidden", "true");
            clone.querySelectorAll<HTMLElement>("a, button, input, select, textarea").forEach(el => el.setAttribute("tabindex", "-1"));
            wrapper.appendChild(clone);
            revealText(clone);
        });
    }

    return unitWidth;
}

function wrap(value: number, unitWidth: number): number {
    if (unitWidth <= 0) return value;

    const wrapped = value % unitWidth;
    return wrapped > 0 ? wrapped - unitWidth : wrapped;
}

function normalizeWheelDelta(event: WheelEvent) {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 20;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * 800;
    return event.deltaY;
}

function bindMarquee(slider: HTMLElement, wrapper: HTMLElement, unitWidth: number) {
    wrapper.style.paddingLeft = `${varGutter}px`;
    slider.style.touchAction = "pan-y";

    let translateX = 0;
    let lastFrameTime = performance.now();
    let wheelVelocity = 0;
    let smoothedVelocity = 0;
    let touchMomentum = 0;
    let isDragging = false;
    let dragStartX = 0;
    let dragStartTranslate = 0;
    let dragLastX = 0;
    let dragLastTime = 0;
    let dragVelocity = 0;
    let isInView = false;
    let pendingDelta = 0;
    let slideVelocity = 0;

    const visibilityObserver = new IntersectionObserver(([entry]) => {
        isInView = entry.isIntersecting;
    });

    const applyTranslate = (x: number) => {
        pendingDelta += x - translateX;
        translateX = wrap(x, unitWidth);
        wrapper.style.transform = `translate3d(${translateX}px, 0, 0)`;
    };

    const onWheel = (event: WheelEvent) => {
        const delta = normalizeWheelDelta(event);
        wheelVelocity = Math.max(-maxWheelVelocity, Math.min(maxWheelVelocity, wheelVelocity + delta * wheelScale));
    };

    const onPointerDown = (event: PointerEvent) => {
        cursor.addState("-active");
        if (!isTouchDevice) return;

        isDragging = true;
        dragStartX = event.clientX;
        dragStartTranslate = translateX;
        dragLastX = event.clientX;
        dragLastTime = performance.now();
        dragVelocity = 0;
        touchMomentum = 0;
    };

    const onPointerMove = (event: PointerEvent) => {
        if (!isDragging) return;

        const now = performance.now();
        const deltaTime = Math.max(1, now - dragLastTime);

        dragVelocity = (event.clientX - dragLastX) / deltaTime;
        dragLastX = event.clientX;
        dragLastTime = now;

        applyTranslate(dragStartTranslate + (event.clientX - dragStartX));
    };

    const onPointerUp = () => {
        cursor.removeState("-active");
        if (!isDragging) return;

        isDragging = false;
        touchMomentum = Math.max(-maxTouchMomentum, Math.min(maxTouchMomentum, dragVelocity * 1000));
    };

    const unbindMarquee = () => {
        visibilityObserver.disconnect();
        window.removeEventListener("wheel", onWheel);
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
    };

    const render = (now: number) => {
        if (!slider.isConnected) {
            unbindMarquee();
            return;
        }

        const deltaSeconds = Math.max(0, Math.min((now - lastFrameTime) / 1000, 0.05));
        lastFrameTime = now;

        wheelVelocity *= Math.exp(-wheelDecay * deltaSeconds);
        if (Math.abs(wheelVelocity) < deadzone) wheelVelocity = 0;

        touchMomentum *= Math.exp(-wheelDecay * deltaSeconds);
        if (Math.abs(touchMomentum) < touchMomentumDeadzone) touchMomentum = 0;

        const isFrozen = slider.closest<HTMLElement>('[data-barba="container"]')?.style.position === "fixed";

        if (!isDragging && isInView && !isFrozen) {
            smoothedVelocity += (wheelVelocity - smoothedVelocity) * Math.min(1, velocitySmoothing * deltaSeconds);
            const velocity = Math.abs(smoothedVelocity) < deadzone ? 0 : smoothedVelocity;
            const vwToPx = referenceWidth / 100;
            const drift = prefersReducedMotion ? 0 : driftSpeedVw;
            const driftPxPerSec = Math.max(-maxSpeedVw, Math.min(maxSpeedVw, drift + velocity * momentumScaleVw)) * vwToPx;

            applyTranslate(translateX + (driftPxPerSec + touchMomentum) * deltaSeconds);
        }

        if (deltaSeconds > 0) {
            const rawSlideVelocity = pendingDelta / deltaSeconds / slideVelocityScale;
            slideVelocity += (rawSlideVelocity - slideVelocity) * Math.min(1, slideVelocitySmoothing * deltaSeconds);
            if (Math.abs(slideVelocity) < slideVelocityDeadzone) slideVelocity = 0;
            slideVelocities.set(wrapper, slideVelocity);
            pendingDelta = 0;
        }

        requestAnimationFrame(render);
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    slider.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    visibilityObserver.observe(slider);

    requestAnimationFrame(render);
}
